import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { kanbanApi, submissionsApi, KanbanColumn, FitLabel, GateStatus } from '../lib/api';
import { useToast } from '../components/ToastHost';
// Phase 2 design system
import { PageHeader, Button, Icon, Badge, Input, Select } from '../components/ui';
import type { BadgeTone } from '../components/ui';

// Solid score-pill accents + gate badge tones, mapped to semantic tokens.
const FIT_SOLID: Record<FitLabel, string> = {
  excellent: 'var(--good)', strong: 'var(--good)', moderate: 'var(--warn)', weak: 'var(--danger)', poor: 'var(--danger)',
};
const GATE_TONE: Record<GateStatus, BadgeTone> = {
  ok: 'good', pending: 'warn', missing: 'danger', unknown: 'neutral',
};

type KanbanCard = KanbanColumn['items'][number];

export default function KanbanBoard() {
  const nav = useNavigate();
  const toast = useToast();
  const [columns, setColumns] = useState<KanbanColumn[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCard, setActiveCard] = useState<KanbanCard | null>(null);
  const [hideTerminal, setHideTerminal] = useState(true);

  // Filters (Phase 6)
  const [searchText, setSearchText] = useState('');
  const [recruiterFilter, setRecruiterFilter] = useState<string>(''); // recruiter_name exact match
  const [jobFilter, setJobFilter] = useState<string>('');              // job_id

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await kanbanApi.get();
      setColumns(res.data.stages);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load kanban');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onDragStart = (e: DragStartEvent) => {
    const id = String(e.active.id);
    const card = columns.flatMap((c) => c.items).find((i) => i.id === id);
    if (card) setActiveCard(card);
  };

  const onDragEnd = async (e: DragEndEvent) => {
    const cardId = String(e.active.id);
    const targetStageKey = e.over ? String(e.over.id) : null;
    setActiveCard(null);
    if (!targetStageKey) return;

    const card = columns.flatMap((c) => c.items).find((i) => i.id === cardId);
    if (!card || card.stage_key === targetStageKey) return;

    // Optimistic update
    const prev = columns;
    const next = columns.map((col) => {
      if (col.key === card.stage_key) {
        return { ...col, items: col.items.filter((i) => i.id !== cardId), count: col.count - 1 };
      }
      if (col.key === targetStageKey) {
        const moved: KanbanCard = { ...card, stage_key: targetStageKey, days_in_stage: 0, is_stale: false };
        return { ...col, items: [moved, ...col.items], count: col.count + 1 };
      }
      return col;
    });
    setColumns(next);

    try {
      const resp = await submissionsApi.moveStage(cardId, targetStageKey);
      const data = resp.data;

      if (targetStageKey === 'placed' && data.placement_created) {
        const bundleCount = data.compliance_bundles_assigned?.length ?? 0;
        const itemsAssigned = data.compliance_bundles_assigned?.reduce((n, b) => n + b.created, 0) ?? 0;
        const parts: string[] = [`Placement created for ${card.candidate_name}`];
        if (bundleCount > 0) {
          parts.push(`${bundleCount} onboarding bundle${bundleCount === 1 ? '' : 's'} (${itemsAssigned} item${itemsAssigned === 1 ? '' : 's'}) assigned`);
        }
        toast.success(parts.join(' · '), {
          action: { label: 'View placement →', onClick: () => nav('/placements') },
          ttl: 8000,
        });
      } else if (targetStageKey === 'placed') {
        // Placement already existed for this submission (idempotent)
        toast.info(`${card.candidate_name} placed — placement already on file`, { ttl: 4000 });
      } else {
        toast.success(`Moved ${card.candidate_name} to "${targetStageKey.replace(/_/g, ' ')}"`, { ttl: 2500 });
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      toast.error(`Move failed: ${e.response?.data?.error ?? e.message ?? 'unknown'}`);
      // Original alert fallback kept for safety during rollback
      void 0;
      setColumns(prev); // rollback
    }
  };

  // Debounce the search box so typing doesn't re-filter up to 1000 cards on
  // every keystroke — the felt lag on this page.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText), 200);
    return () => clearTimeout(t);
  }, [searchText]);

  // Filter option sets depend only on the loaded cards — memoize so they don't
  // rebuild on every unrelated render (filter changes, DnD, etc.).
  const allCards = useMemo(() => columns.flatMap((c) => c.items), [columns]);
  const recruiterOptions = useMemo(
    () => Array.from(new Set(allCards.map((c) => c.recruiter_name).filter((v): v is string => !!v))).sort(),
    [allCards],
  );
  const jobOptions = useMemo(
    () => Array.from(
      new Map(allCards.map((c) => [c.job_id, { job_id: c.job_id, job_title: c.job_title, job_code: c.job_code }])).values()
    ).sort((a, b) => (a.job_title ?? '').localeCompare(b.job_title ?? '')),
    [allCards],
  );

  // Apply filters (client-side — the /kanban endpoint returns up to 1000 rows).
  // Memoized on the actual filter inputs so it only recomputes when they change.
  const filteredColumns = useMemo(() => {
    const searchLower = debouncedSearch.trim().toLowerCase();
    const cardMatches = (card: KanbanCard): boolean => {
      if (recruiterFilter && card.recruiter_name !== recruiterFilter) return false;
      if (jobFilter && card.job_id !== jobFilter) return false;
      if (searchLower) {
        const hay = [
          card.candidate_name, card.job_title, card.job_code,
          card.client_name, card.facility_name, card.candidate_role,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(searchLower)) return false;
      }
      return true;
    };
    return columns.map((col) => {
      const items = col.items.filter(cardMatches);
      return { ...col, items, count: items.length };
    });
  }, [columns, debouncedSearch, recruiterFilter, jobFilter]);

  const visibleColumns = useMemo(
    () => (hideTerminal ? filteredColumns.filter((c) => !c.is_terminal) : filteredColumns),
    [filteredColumns, hideTerminal],
  );
  const totalCards = filteredColumns.reduce((n, c) => n + c.count, 0);
  const totalUnfiltered = columns.reduce((n, c) => n + c.count, 0);
  const filtersActive = !!(searchText || recruiterFilter || jobFilter);

  return (
    <div style={{ padding: '0 0', height: 'calc(100vh - 60px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <PageHeader
        icon={<Icon name="placement" size={22} />}
        title="Pipeline"
        subtitle={
          loading
            ? 'Loading…'
            : filtersActive
              ? `${totalCards} of ${totalUnfiltered} submission${totalUnfiltered === 1 ? '' : 's'} match filters`
              : `${totalCards} submission${totalCards === 1 ? '' : 's'} across ${visibleColumns.length} stage${visibleColumns.length === 1 ? '' : 's'}`
        }
        actions={
          <>
            <label style={{ fontSize: 12, color: 'var(--t2)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={hideTerminal} onChange={(e) => setHideTerminal(e.target.checked)} />
              Hide terminal stages
            </label>
            <Button variant="secondary" size="sm" icon={<Icon name="refresh" size={15} />} onClick={load}>
              Refresh
            </Button>
          </>
        }
      />

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexShrink: 0, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder="Search candidate / job / client…"
          style={{ flex: '1 1 240px', minWidth: 200 }}
        />
        <Select
          value={recruiterFilter}
          onChange={(e) => setRecruiterFilter(e.target.value)}
          style={{ width: 'auto', minWidth: 140, flex: '0 0 auto' }}
          aria-label="Filter by recruiter"
        >
          <option value="">All recruiters</option>
          {recruiterOptions.map((r) => <option key={r} value={r}>{r}</option>)}
        </Select>
        <Select
          value={jobFilter}
          onChange={(e) => setJobFilter(e.target.value)}
          style={{ width: 'auto', minWidth: 180, maxWidth: 260, flex: '0 0 auto' }}
          aria-label="Filter by job"
        >
          <option value="">All jobs</option>
          {jobOptions.map((j) => (
            <option key={j.job_id} value={j.job_id}>
              {j.job_code ? `${j.job_code} — ` : ''}{j.job_title}
            </option>
          ))}
        </Select>
        {filtersActive && (
          <Button variant="ghost" size="sm" onClick={() => { setSearchText(''); setRecruiterFilter(''); setJobFilter(''); }}>
            Clear filters
          </Button>
        )}
      </div>

      {error && <div style={{ padding: 10, background: 'var(--danger-tint)', color: 'var(--danger-fg)', borderRadius: 'var(--r-md)', marginBottom: 10 }}>{error}</div>}

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--t3)' }}>Loading…</div>
      ) : (
        <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
          <div style={{ display: 'flex', gap: 10, overflowX: 'auto', flex: 1, paddingBottom: 8 }}>
            {visibleColumns.map((col) => (
              <StageColumn
                key={col.key}
                column={col}
                onCardClick={(cardId) => nav(`/submissions/${cardId}`)}
              />
            ))}
          </div>
          <DragOverlay>
            {activeCard && <SubmissionCard card={activeCard} dragging />}
          </DragOverlay>
        </DndContext>
      )}
    </div>
  );
}

// ─── Column (droppable) ─────────────────────────────────────────────────────
function StageColumn({ column, onCardClick }: { column: KanbanColumn; onCardClick: (id: string) => void }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.key });
  const accent = column.color ?? 'var(--t3)';

  return (
    <div
      style={{
        width: 280,
        flexShrink: 0,
        background: isOver ? `color-mix(in srgb, ${accent} 12%, var(--sf2))` : 'var(--sf2)',
        borderRadius: 'var(--r-lg)',
        border: `1px solid ${isOver ? accent : 'var(--bd)'}`,
        display: 'flex',
        flexDirection: 'column',
        boxShadow: isOver ? 'var(--e-2)' : 'var(--e-1)',
        transition: 'background 0.15s, border-color 0.15s, box-shadow 0.15s',
      }}
    >
      <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--bd)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: accent }} />
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--t1)' }}>{column.label}</span>
        </div>
        <span style={{ fontSize: 11, color: 'var(--t3)', fontWeight: 600, padding: '1px 8px', background: 'var(--sf)', borderRadius: 'var(--r-full)' }}>
          {column.count}
        </span>
      </div>
      <div ref={setNodeRef} style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto', flex: 1, minHeight: 100 }}>
        {column.items.length === 0 ? (
          <div style={{ padding: 20, textAlign: 'center', fontSize: 11, color: 'var(--t3)' }}>Drop here</div>
        ) : (
          column.items.map((item) => (
            <DraggableCard key={item.id} card={item} onClick={() => onCardClick(item.id)} />
          ))
        )}
      </div>
    </div>
  );
}

// ─── Draggable card wrapper ─────────────────────────────────────────────────
function DraggableCard({ card, onClick }: { card: KanbanCard; onClick: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: card.id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={(e) => { if (!isDragging) onClick(); e.stopPropagation(); }}
      style={{ opacity: isDragging ? 0.4 : 1, cursor: isDragging ? 'grabbing' : 'grab' }}
    >
      <SubmissionCard card={card} />
    </div>
  );
}

// ─── Card visual (used in both the column and the DragOverlay) ──────────────
function SubmissionCard({ card, dragging }: { card: KanbanCard; dragging?: boolean }) {
  return (
    <div
      style={{
        background: 'var(--sf)',
        borderRadius: 'var(--r-md)',
        border: card.is_stale ? '1px solid var(--warn)' : '1px solid var(--bd)',
        boxShadow: dragging ? 'var(--sh2)' : 'var(--e-1)',
        padding: 10,
        fontSize: 12,
        color: 'var(--t1)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 6, marginBottom: 4 }}>
        <span style={{ fontWeight: 600, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
          {card.candidate_name}
        </span>
        {card.ai_score != null && (
          <span
            style={{
              fontSize: 10, fontWeight: 700, color: '#fff',
              background: card.ai_fit_label ? FIT_SOLID[card.ai_fit_label] : 'var(--t3)',
              padding: '1px 6px', borderRadius: 'var(--r-full)', flexShrink: 0,
            }}
          >
            {card.ai_score}
          </span>
        )}
      </div>
      <div style={{ color: 'var(--t3)', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: 4 }}>
        {card.job_title}
      </div>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
        {card.gate_status && card.gate_status !== 'unknown' && (
          <Badge
            tone={GATE_TONE[card.gate_status]}
            title={`Gate: ${card.gate_status}`}
            style={{ fontSize: 9, textTransform: 'uppercase', padding: '1px 6px' }}
          >
            {card.gate_status}
          </Badge>
        )}
        {card.is_stale && (
          <span style={{ fontSize: 9, color: 'var(--warn-fg)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 2 }}>
            <Icon name="alert" size={10} /> {card.days_in_stage}d
          </span>
        )}
        {!card.is_stale && (
          <span style={{ fontSize: 9, color: 'var(--t3)' }}>{card.days_in_stage}d</span>
        )}
        {card.recruiter_name && (
          <span style={{ fontSize: 9, color: 'var(--t3)' }}>· {card.recruiter_name}</span>
        )}
      </div>
    </div>
  );
}
